import { useEffect, useRef, useState } from "react";
import {
  ArrowUpCircle, Bug, Check, ChevronLeft, ChevronRight, ChevronsUpDown, ExternalLink, Gauge, Globe, HelpCircle, Info, KeyRound, LogOut, MonitorSmartphone, Settings,
} from "lucide-react";
import { useAuth } from "@/App";
import UserAvatar from "@/components/UserAvatar";

// Menu du compte, en bas à gauche (comme sur claude.ai) : réglages, utilisation,
// langue des réponses, aide, offre, application, liens utiles, clés API,
// déconnexion. Bouton à côté : signaler un problème.

export const LANGUAGES = [
  ["auto", "Automatique", "Langue de ton message"],
  ["fr", "Français", ""],
  ["en", "English", ""],
  ["es", "Español", ""],
  ["de", "Deutsch", ""],
  ["it", "Italiano", ""],
  ["pt", "Português", ""],
  ["ar", "العربية", ""],
];

const LINKS = [
  ["Conditions d'utilisation", "/conditions-utilisation"],
  ["Politique de confidentialité", "/politique-confidentialite"],
  ["Mentions légales", "/mentions-legales"],
  ["Statut des services", "/status"],
  ["Site VantaPulse", "/"],
];

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

function Item({ icon: Icon, label, hint, onClick, href, external, chevron, testId }) {
  const cls = "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/85 hover:bg-white/[0.06] hover:text-white text-left";
  const body = (
    <>
      <Icon className="w-[18px] h-[18px] text-white/60 shrink-0" />
      <span className="flex-1 min-w-0 truncate">{label}</span>
      {hint && <span className="text-[11px] text-white/35 shrink-0">{hint}</span>}
      {external && <ExternalLink className="w-3.5 h-3.5 text-white/40 shrink-0" />}
      {chevron && <ChevronRight className="w-4 h-4 text-white/40 shrink-0" />}
    </>
  );
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testId}>{body}</a>
    : <button type="button" onClick={onClick} className={cls} data-testid={testId}>{body}</button>;
}

const Divider = () => <div className="my-1 h-px bg-white/[0.07]" />;

export default function AccountMenu({ me, collapsed, onSettings, onUpgrade, onLanguage, onInstall, onFeedback, onLogout }) {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState("main"); // main | language | more
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  useEffect(() => { if (!open) setPanel("main"); }, [open]);

  const { profile } = useAuth();
  const name = me.settings.name || me.user.username || me.user.email;
  const act = (fn) => () => { setOpen(false); fn(); };
  const language = me.settings.language || "auto";

  return (
    <div ref={ref} className="relative">
      {open && (
        <div className={`vai-pop absolute bottom-full mb-2 z-50 w-72 rounded-xl border border-white/10 bg-[#1a1a22] shadow-2xl p-1.5 left-0`} data-testid="vai-account-menu" role="menu">
          {panel === "main" && (
            <>
              <p className="px-3 pt-1.5 pb-2 text-sm text-white/45 truncate">{me.user.email}</p>
              <Item icon={Settings} label="Paramètres" hint={isMac ? "⇧⌘," : "Ctrl+Shift+,"} onClick={act(() => onSettings("general"))} testId="vai-menu-settings" />
              <Item icon={Gauge} label="Utilisation" onClick={act(() => onSettings("usage"))} testId="vai-menu-usage" />
              <Item icon={Globe} label="Langue" chevron onClick={() => setPanel("language")} testId="vai-menu-language" />
              <Item icon={HelpCircle} label="Obtenir de l'aide" href="/support" testId="vai-menu-help" />
              <Divider />
              {me.plan.id !== "max" && <Item icon={ArrowUpCircle} label="Mettre le forfait à niveau" onClick={act(onUpgrade)} testId="vai-menu-upgrade" />}
              <Item icon={MonitorSmartphone} label="Applications et intégrations" onClick={act(onInstall)} testId="vai-menu-install" />
              <Item icon={Info} label="En savoir plus" chevron onClick={() => setPanel("more")} testId="vai-menu-more" />
              <Divider />
              <Item icon={KeyRound} label="Obtenir des clés API" href="/ai/api" external testId="vai-menu-api" />
              <Divider />
              <Item icon={LogOut} label="Se déconnecter" onClick={act(onLogout)} testId="vai-menu-logout" />
            </>
          )}
          {panel === "language" && (
            <>
              <button type="button" onClick={() => setPanel("main")} className="w-full flex items-center gap-2 px-2 py-2 text-sm text-white/60 hover:text-white"><ChevronLeft className="w-4 h-4" />Langue des réponses</button>
              <Divider />
              {LANGUAGES.map(([id, label, hint]) => (
                <button key={id} type="button" onClick={act(() => onLanguage(id))} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-white/[0.06] text-left" data-testid={`vai-lang-${id}`}>
                  <span className="flex-1">{label}{hint && <span className="block text-[11px] text-white/40">{hint}</span>}</span>
                  {language === id && <Check className="w-4 h-4 text-sky-300" />}
                </button>
              ))}
            </>
          )}
          {panel === "more" && (
            <>
              <button type="button" onClick={() => setPanel("main")} className="w-full flex items-center gap-2 px-2 py-2 text-sm text-white/60 hover:text-white"><ChevronLeft className="w-4 h-4" />En savoir plus</button>
              <Divider />
              {LINKS.map(([label, href]) => (
                <a key={href} href={href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm text-white/85 hover:bg-white/[0.06]">
                  {label}<ExternalLink className="w-3.5 h-3.5 text-white/40" />
                </a>
              ))}
            </>
          )}
        </div>
      )}

      <div className={`flex items-center ${collapsed ? "justify-center" : "gap-1"}`}>
        <button type="button" onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open}
          className={`flex items-center gap-2.5 rounded-xl hover:bg-white/[0.06] ${collapsed ? "p-1.5" : "flex-1 min-w-0 px-2 py-2"} ${open ? "bg-white/[0.06]" : ""}`} data-testid="vai-account">
          {/* Même photo de profil que sur le site principal. */}
          {profile ? <UserAvatar profile={profile} size={32} className="shrink-0" />
            : <span className="w-8 h-8 rounded-full bg-white/90 text-[#0e0e14] flex items-center justify-center text-sm font-semibold shrink-0">{(name || "?").slice(0, 1).toUpperCase()}</span>}
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1 text-left text-sm truncate">
                <span className="text-white">{name}</span><span className="text-white/45"> · {me.plan.name}</span>
              </span>
              <ChevronsUpDown className="w-4 h-4 text-white/45 shrink-0" />
            </>
          )}
        </button>
        {!collapsed && (
          <button type="button" onClick={onFeedback} className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/[0.06] shrink-0" aria-label="Signaler un problème" title="Signaler un problème" data-testid="vai-feedback">
            <Bug className="w-[18px] h-[18px]" />
          </button>
        )}
      </div>
    </div>
  );
}
